import { useEffect, useMemo, useState } from "react";
import { endpoints } from "@/lib/api";
import { BarChart3, Info } from "lucide-react";

const WINDOWS = [
    { key: "7", label: "7d" },
    { key: "30", label: "30d" },
    { key: "90", label: "90d" },
    { key: "all", label: "All" },
];

export default function FollowerSourceBreakdown({ artistId }) {
    const [win, setWin] = useState("30");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!artistId) return;
        setLoading(true);
        endpoints.artistFollowerSources(artistId, win)
            .then((d) => setData(d))
            .finally(() => setLoading(false));
    }, [artistId, win]);

    const max = useMemo(() => {
        if (!data) return 1;
        return Math.max(1, ...data.sources.map((s) => s.count_window));
    }, [data]);

    return (
        <section className="bg-[#0a0a0a] p-6 border-t border-[#2a2a2a]" data-testid="follower-sources-section">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                    <div className="text-[10px] font-mono uppercase text-[#666] flex items-center gap-1"><BarChart3 className="w-3 h-3" /> Attribution</div>
                    <h2 className="mt-1 font-display font-black uppercase text-xl md:text-2xl">Follower sources</h2>
                </div>
                <div className="flex border border-[#2a2a2a]" role="tablist" data-testid="follower-source-window-filter">
                    {WINDOWS.map((w) => (
                        <button
                            key={w.key}
                            onClick={() => setWin(w.key)}
                            data-testid={`follower-source-window-${w.key}`}
                            aria-pressed={win === w.key}
                            className={`px-3 py-1 text-[10px] font-mono uppercase transition-colors ${win === w.key ? "bg-[#ff4400] text-black" : "text-[#a3a3a3] hover:text-[#f4f4f0]"}`}
                        >
                            {w.label}
                        </button>
                    ))}
                </div>
            </div>

            {loading || !data ? (
                <div className="mt-4 text-xs font-mono uppercase text-[#666]" data-testid="follower-sources-loading">Loading sources…</div>
            ) : (
                <>
                    <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-px bg-[#2a2a2a] border border-[#2a2a2a]" data-testid="follower-sources-summary">
                        <SummaryCell label="Follows · window" value={data.window_total} sub={data.window_label} testId="fs-window-total" />
                        <SummaryCell
                            label="Top source"
                            value={data.top_source?.label || "—"}
                            sub={data.top_source ? `${data.top_source.count_window} · ${data.top_source.percentage_window}%` : "No tracked follows"}
                            testId="fs-top"
                        />
                        <SummaryCell label="Direct from event" value={data.direct_event_follows_window} sub="via event pages" testId="fs-direct-event" />
                        <SummaryCell label="Tracked · all time" value={data.all_time_total} sub={data.legacy_excluded ? `${data.legacy_excluded} legacy excluded` : "excludes legacy"} testId="fs-all-total" />
                    </div>

                    <div className="mt-4 space-y-3" data-testid="follower-sources-bars">
                        {data.sources.map((s) => {
                            const width = Math.round((s.count_window / max) * 100);
                            return (
                                <div key={s.key} data-testid={`fs-row-${s.key}`}>
                                    <div className="flex items-center justify-between text-xs font-mono uppercase">
                                        <span className="text-[#f4f4f0]">{s.label}</span>
                                        <span className="text-[#a3a3a3]">
                                            <span className="text-[#f4f4f0]" data-testid={`fs-count-${s.key}`}>{s.count_window}</span>
                                            <span className="mx-2 opacity-40">·</span>
                                            <span data-testid={`fs-pct-${s.key}`}>{s.percentage_window}%</span>
                                        </span>
                                    </div>
                                    <div className="h-2 bg-[#141414] mt-1">
                                        <div className={`h-full ${s.count_window > 0 ? "bg-[#ff4400]" : "bg-[#2a2a2a]"} transition-all`} style={{ width: `${width}%` }} />
                                    </div>
                                    <div className="mt-1 text-[10px] font-mono uppercase text-[#666]">
                                        7d {s.count_7} · 30d {s.count_30} · 90d {s.count_90} · all-time {s.total_all_time}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <div className="mt-4 border border-[#2a2a2a] p-3 text-[10px] font-mono uppercase text-[#a3a3a3] flex items-start gap-2" data-testid="follower-sources-note">
                        <Info className="w-3 h-3 mt-0.5" /> {data.note_legacy}
                    </div>
                </>
            )}
        </section>
    );
}

function SummaryCell({ label, value, sub, testId }) {
    return (
        <div className="bg-[#0a0a0a] p-4" data-testid={testId}>
            <div className="text-[10px] font-mono uppercase text-[#666]">{label}</div>
            <div className="mt-1 font-display font-black uppercase text-2xl md:text-3xl truncate">{value}</div>
            {sub && <div className="text-[10px] font-mono uppercase text-[#a3a3a3] mt-1 truncate">{sub}</div>}
        </div>
    );
}
